import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface OrderLoadResult {
  order: string[] | null;
  previousOrder?: string[] | null;
}

function readCached(storageKey: string): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * A per-user ordering saved on the server, with a localStorage cache for instant
 * first paint. `load` and `save` must be stable references (module-level functions).
 */
export function usePersistedOrder(
  storageKey: string,
  load: () => Promise<OrderLoadResult>,
  save: (order: string[]) => Promise<unknown>,
): {
  order: string[];
  setOrder: (order: string[]) => void;
  previousOrder: string[] | null;
} {
  const { user } = useAuth();
  const [order, setOrderState] = useState<string[]>(() => readCached(storageKey));
  const [previousOrder, setPreviousOrder] = useState<string[] | null>(null);
  const orderRef = useRef(order);
  const userId = user?.userId ?? null;

  useEffect(() => {
    orderRef.current = order;
  }, [order]);

  // Persist locally on every change so the next load paints correctly before
  // the server sync resolves.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(storageKey, JSON.stringify(order));
  }, [order, storageKey]);

  // Reconcile with the server once per authenticated user: adopt the server
  // value if one was ever saved (an empty array is a Reset, and must stick),
  // otherwise backfill from the local cache.
  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    (async () => {
      try {
        const result = await load();
        if (cancelled) return;
        setPreviousOrder(result.previousOrder ?? null);
        if (Array.isArray(result.order)) {
          setOrderState(result.order);
        } else if (orderRef.current.length > 0) {
          await save(orderRef.current);
        }
      } catch (err) {
        console.warn(`Order preference sync failed (${storageKey})`, err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, load, save, storageKey]);

  const setOrder = useCallback(
    (next: string[]) => {
      setOrderState(next);
      save(next).catch((err) => {
        console.warn(`Failed to persist order preference (${storageKey})`, err);
      });
    },
    [save, storageKey],
  );

  return { order, setOrder, previousOrder };
}
